// Schedule and playoffs on a simulated league (SCHEDULE.md): the season as
// a list of periods, and the bracket, projected from the standings during
// the regular season and live once the playoff periods arrive.
//
// THE SHAPE FUNCTIONS MOVED. `bracketShape`, `bracketOrder` and `roundName`
// lived here until 2026-09-19 with a note saying they would move to
// `@fsp/domain` when the API was built. BACKLOG E9a built it, so they are
// imported from there and re-exported for the screens that already import them
// from this package. A real league and a mocked one draw the same bracket
// because they now call the same three functions.

import type {
  BracketGame,
  BracketRound,
  BracketShape,
  BracketSlot,
  BracketView,
  MatchupSummary,
  ScheduleView,
  SeedRow,
  TeamRef,
} from '@fsp/domain';
import {
  bracketOrder,
  bracketShape,
  consolationField,
  roundName,
  scheduleStrength,
} from '@fsp/domain';
import type { SimMatchup, SimState } from './league';
import { leagueRef, matchupSummary, periodRef, standingsView, statsOf, teamRef } from './views';

export { bracketOrder, bracketShape, roundName };
export type { BracketShape, SeedRow };

/** A pairing in the bracket: seeds, or the games whose winners meet. */
interface Pair {
  top: { seed: number } | { from: string };
  bottom: { seed: number } | { from: string };
  id: string;
}

/**
 * Every round's pairings for a seeded field. Without reseeding the tree is
 * fixed (1 vs 8, 4 vs 5 feed one semifinal); with reseeding each round pairs
 * the highest remaining seed with the lowest, decided when the round comes.
 */
function pairings(shape: BracketShape, prefix: string): Pair[][] {
  const rounds: Pair[][] = [];
  const first: Pair[] = [];
  // Standard bracket order so 1 and 2 are on opposite halves: 1v8, 4v5, 2v7, 3v6.
  const order = bracketOrder(shape.size);
  for (let i = 0; i < order.length; i += 2)
    first.push({
      top: { seed: order[i]! },
      bottom: { seed: order[i + 1]! },
      id: `${prefix}r1g${i / 2 + 1}`,
    });
  rounds.push(first);
  let prev = first;
  for (let r = 2; r <= shape.rounds; r++) {
    const next: Pair[] = [];
    for (let i = 0; i < prev.length; i += 2)
      next.push({
        top: { from: prev[i]!.id },
        bottom: { from: prev[i + 1]!.id },
        id: `${prefix}r${r}g${i / 2 + 1}`,
      });
    rounds.push(next);
    prev = next;
  }
  return rounds;
}

/**
 * Playoff seeds from the standings through the regular season, and the
 * consolation field after them: a real league's (`consolationField`, B-528),
 * the best of the teams that missed in a consolation bracket, the worst first
 * in a toilet bowl, as many as a bracket no deeper than the winners' holds.
 */
export function seedFields(
  state: SimState,
  now: number,
): { playoff: SeedRow[]; consolation: SeedRow[] } {
  const rows = standingsView(state, now).rows;
  const n = Math.min(state.settings.playoffs.teamCount, rows.length);
  const playoff = rows.slice(0, n).map((r, i) => ({ seed: i + 1, teamId: r.team.id }));
  const consolation = consolationField(
    rows.map((r, i) => ({ teamId: r.team.id, rank: i + 1 })),
    state.settings.playoffs,
  );
  return { playoff, consolation };
}

interface Resolved {
  slot: BracketSlot | null;
  /** The team that came out of this game, when decided. */
  winnerTeamId: string | null;
}

/**
 * Build one bracket (playoff or consolation) as rounds of games, resolving
 * each game from the schedule and the finals. `loserAdvances` is the toilet
 * bowl. Games whose teams are known but unplayed are `pre`; unknown are `tbd`.
 */
function buildBracket(
  state: SimState,
  now: number,
  field: SeedRow[],
  prefix: string,
  loserAdvances: boolean,
  refs: Map<string, TeamRef>,
  /** Rounds the winners' bracket plays before this one starts: a consolation's final is the championship's week. */
  offset = 0,
): BracketRound[] {
  const shape = bracketShape(field.length);
  if (shape.rounds === 0) return [];
  const periods = state.settings.playoffs.periods.slice(offset);
  const reseed = state.settings.playoffs.reseed;
  const rounds = pairings(shape, prefix);
  const bySeed = new Map(field.map((f) => [f.seed, f.teamId]));
  const outcome = new Map<string, Resolved>();
  const out: BracketRound[] = [];

  // With reseeding, round r's pairings are recomputed from the survivors by seed.
  let survivors: { seed: number; teamId: string }[] | null = null;

  rounds.forEach((pairs, r) => {
    const period =
      periods[r] ??
      (periods[periods.length - 1] ?? state.regularSeasonEnd + r + 1) + (r - periods.length + 1);
    const games: BracketGame[] = [];
    let live: Pair[] = pairs;
    if (reseed && r > 0 && survivors && survivors.every((s) => s.teamId)) {
      const sorted = [...survivors].sort((a, b) => a.seed - b.seed);
      live = [];
      for (let i = 0; i < sorted.length / 2; i++)
        live.push({
          top: { seed: sorted[i]!.seed },
          bottom: { seed: sorted[sorted.length - 1 - i]!.seed },
          id: `${prefix}r${r + 1}g${i + 1}`,
        });
    }
    const nextSurvivors: { seed: number; teamId: string }[] = [];
    for (const pair of live) {
      const side = (
        s: Pair['top'],
      ): { slot: BracketSlot | null; source: string | null; bye: boolean } => {
        if ('seed' in s) {
          const teamId = bySeed.get(s.seed);
          if (!teamId) return { slot: null, source: null, bye: true };
          return {
            slot: { seed: s.seed, team: refs.get(teamId)!, source: null },
            source: null,
            bye: false,
          };
        }
        const from = outcome.get(s.from);
        if (from?.winnerTeamId) {
          const seed = field.find((f) => f.teamId === from.winnerTeamId)!.seed;
          return {
            slot: { seed, team: refs.get(from.winnerTeamId)!, source: null },
            source: null,
            bye: false,
          };
        }
        const g = out.flatMap((x) => x.games).find((x) => x.id === s.from);
        return {
          slot: null,
          source: g ? `${loserAdvances ? 'Loser' : 'Winner'} of ${g.label}` : 'To be decided',
          bye: false,
        };
      };
      const top = side(pair.top);
      const bottom = side(pair.bottom);
      // A bye: the seeded team rests and advances.
      if (top.bye || bottom.bye) {
        const rester = top.bye ? bottom.slot : top.slot;
        const game: BracketGame = {
          id: pair.id,
          label: rester ? `${rester.seed} rests` : 'Bye',
          top: rester,
          bottom: null,
          matchupId: null,
          status: 'bye',
          winnerTeamId: rester?.team.id ?? null,
          topScore: null,
          bottomScore: null,
        };
        games.push(game);
        outcome.set(pair.id, { slot: rester, winnerTeamId: rester?.team.id ?? null });
        if (rester) nextSurvivors.push({ seed: rester.seed, teamId: rester.team.id });
        continue;
      }
      const label =
        top.slot && bottom.slot
          ? `${top.slot.seed} vs ${bottom.slot.seed}`
          : `${top.source ?? '?'} / ${bottom.source ?? '?'}`;
      let matchupId: string | null = null;
      let status: BracketGame['status'] = 'tbd';
      let winnerTeamId: string | null = null;
      let topScore: number | null = null;
      let bottomScore: number | null = null;
      if (top.slot && bottom.slot) {
        const m = state.schedule.find(
          (x) =>
            x.period === period &&
            ((x.home === top.slot!.team.id && x.away === bottom.slot!.team.id) ||
              (x.away === top.slot!.team.id && x.home === bottom.slot!.team.id)),
        );
        if (m) {
          matchupId = m.id;
          const s = matchupSummary(state, m.id, now);
          status = s.status;
          const topIsHome = s.home.team.id === top.slot.team.id;
          if (s.status !== 'pre') {
            topScore = topIsHome ? s.home.score : s.away.score;
            bottomScore = topIsHome ? s.away.score : s.home.score;
          }
          if (s.status === 'final') {
            const topWon = (topScore ?? 0) >= (bottomScore ?? 0);
            const advancing = topWon !== loserAdvances ? top.slot.team.id : bottom.slot.team.id;
            winnerTeamId = advancing;
          }
        } else status = 'pre';
      }
      games.push({
        id: pair.id,
        label,
        top: top.slot
          ? { ...top.slot }
          : top.source
            ? { seed: 0, team: placeholder(top.source), source: top.source }
            : null,
        bottom: bottom.slot
          ? { ...bottom.slot }
          : bottom.source
            ? { seed: 0, team: placeholder(bottom.source), source: bottom.source }
            : null,
        matchupId,
        status,
        winnerTeamId,
        topScore,
        bottomScore,
      });
      outcome.set(pair.id, { slot: null, winnerTeamId });
      if (winnerTeamId)
        nextSurvivors.push({
          seed: field.find((f) => f.teamId === winnerTeamId)!.seed,
          teamId: winnerTeamId,
        });
    }
    survivors = nextSurvivors.length === live.length ? nextSurvivors : null;
    out.push({
      name: roundName(r, shape.rounds, prefix === 'c'),
      // A consolation round's week is the consolation's, as a real league's read says it.
      period:
        prefix === 'c'
          ? { ...periodRef(state, period), kind: 'consolation' }
          : periodRef(state, period),
      games,
    });
  });
  return out;
}

/** How many winners' rounds come before a consolation bracket's first (its final the championship's week). */
function offsetOf(playoff: readonly SeedRow[], consolation: readonly SeedRow[]): number {
  return Math.max(0, bracketShape(playoff.length).rounds - bracketShape(consolation.length).rounds);
}

function placeholder(source: string): TeamRef {
  return {
    id: `tbd:${source}`,
    name: source,
    abbreviation: '?',
    ownerName: '',
    colors: { primary: '#333333', secondary: '#555555' },
    badge: null,
    banner: null,
    record: { wins: 0, losses: 0, ties: 0 },
    isMine: false,
    titles: 0,
  };
}

/**
 * The playoff and consolation matchups for a period, from the bracket as
 * it stands through the previous round. Called at creation for every
 * playoff period up to the current one, so a league created at Week 16 has
 * its semifinals in the schedule.
 */
export function playoffMatchupsFor(state: SimState, period: number, now: number): SimMatchup[] {
  const refs = new Map(state.teams.map((t) => [t.id, teamRef(state, t, now)]));
  const { playoff, consolation } = seedFields(state, now);
  const out: SimMatchup[] = [];
  const fromRounds = (rounds: BracketRound[], prefix: string) => {
    for (const round of rounds) {
      if (round.period.index !== period) continue;
      round.games.forEach((g, i) => {
        if (g.status === 'bye' || !g.top || !g.bottom || g.top.seed === 0 || g.bottom.seed === 0)
          return;
        out.push({
          id: `${prefix}${period}-${i + 1}`,
          period,
          home: g.top.team.id,
          away: g.bottom.team.id,
        });
      });
    }
  };
  fromRounds(buildBracket(state, now, playoff, 'p', false, refs), 'm');
  const kind = state.settings.playoffs.consolation;
  if (kind !== 'none')
    fromRounds(
      buildBracket(
        state,
        now,
        consolation,
        'c',
        kind === 'toilet-bowl',
        refs,
        offsetOf(playoff, consolation),
      ),
      'c',
    );
  return out;
}

export function bracketView(state: SimState, now: number): BracketView {
  const refs = new Map(state.teams.map((t) => [t.id, teamRef(state, t, now)]));
  const { playoff, consolation } = seedFields(state, now);
  const rounds = buildBracket(state, now, playoff, 'p', false, refs);
  const kind = state.settings.playoffs.consolation;
  const cons =
    kind === 'none' || consolation.length < 2
      ? null
      : buildBracket(
          state,
          now,
          consolation,
          'c',
          kind === 'toilet-bowl',
          refs,
          offsetOf(playoff, consolation),
        );
  const finalGame = rounds[rounds.length - 1]?.games[0];
  const champion =
    finalGame?.status === 'final' && finalGame.winnerTeamId
      ? (refs.get(finalGame.winnerTeamId) ?? null)
      : null;
  const started = state.currentPeriod > state.regularSeasonEnd;
  const status: BracketView['status'] = champion ? 'final' : started ? 'live' : 'projected';
  const standings = standingsView(state, now);
  const trophy = state.settings.identity.branding.trophyName;
  const current = rounds.find((r) => r.period.index === state.currentPeriod);
  const headline =
    status === 'final'
      ? `${trophy}: ${champion!.name}.`
      : status === 'live'
        ? `${current?.name ?? 'Playoffs'} · Week ${state.currentPeriod}`
        : 'Playoff picture · if the season ended today';
  return {
    league: leagueRef(state),
    status,
    headline,
    teamCount: playoff.length,
    byes: bracketShape(playoff.length).byes,
    rounds,
    consolation: cons,
    consolationKind: kind,
    champion,
    seeds: playoff.map((s) => ({
      seed: s.seed,
      team: refs.get(s.teamId)!,
      status: standings.rows.find((r) => r.team.id === s.teamId)?.playoffStatus ?? 'alive',
    })),
    throughPeriod: Math.min(state.currentPeriod - 1, state.regularSeasonEnd),
    updatedAt: new Date(now).toISOString(),
  };
}

export function scheduleView(state: SimState, now: number): ScheduleView {
  const mine = state.teams.find((t) => t.isMine)!;
  const standings = standingsView(state, now);
  const myRow = standings.rows.find((r) => r.team.id === mine.id);
  const last = Math.max(state.regularSeasonEnd, ...state.settings.playoffs.periods);
  const periods: ScheduleView['periods'] = [];
  for (let p = 1; p <= last; p++) {
    const ms = state.schedule.filter((m) => m.period === p);
    const summaries: MatchupSummary[] = ms.map((m) => matchupSummary(state, m.id, now));
    const my =
      summaries.find((s) => s.home.team.id === mine.id || s.away.team.id === mine.id) ?? null;
    const kind: ScheduleView['periods'][number]['kind'] =
      p <= state.regularSeasonEnd
        ? 'regular'
        : my?.kind === 'consolation'
          ? 'consolation'
          : 'playoff';
    periods.push({
      period: periodRef(state, p),
      kind,
      rivalry: state.settings.schedule.rivalryPeriods.includes(p),
      myMatchup: my,
      // TODO(Q6): the practice league plays one opponent a week.
      mySecond: null,
      myField: null,
      myBye: p <= state.regularSeasonEnd && my === null,
      matchups: summaries,
      stats: statsOf(state, p, now),
    });
  }
  return {
    league: leagueRef(state),
    myTeamId: mine.id,
    record: {
      wins: myRow?.wins ?? 0,
      losses: myRow?.losses ?? 0,
      ties: myRow?.ties ?? 0,
      rank: myRow?.rank ?? 0,
      of: standings.rows.length,
    },
    regularSeasonEnd: state.regularSeasonEnd,
    playoffTeamCount: state.settings.playoffs.teamCount,
    periods,
    // The league store's rule, from the same weeks (B-386).
    strength: scheduleStrength(periods),
    // The practice league plays from its first week, so it has no schedule
    // still to be set.
    upcoming: null,
    updatedAt: new Date(now).toISOString(),
  };
}
